import { useNavigate } from '@tanstack/react-router';
import { toast } from '@workspace/ui/components/sonner';
import { useCallback } from 'react';
import type { OriginName } from '~/interfaces/common';
import { fetchAuthRegistration } from '~/routes/auth/signup/-api/api';
import type { AccountProfileData } from '~/routes/auth/signup/-types/types';
import { setLocalStorageItem } from '~/utils/local-storage';
import type { Invite } from '../-types/invite';

type InviteChannel = 'whatsapp' | 'email';

const CHANNEL_CONFIG: Record<
  InviteChannel,
  { provider: OriginName; otpRoute: string }
> = {
  whatsapp: { provider: 'bloque-whatsapp', otpRoute: '/auth/otp/whatsapp' },
  email: { provider: 'bloque-email', otpRoute: '/auth/otp/email' },
};

export const useInviteProcessing = () => {
  const navigate = useNavigate();

  const createSignupData = useCallback(
    (data: Invite): AccountProfileData => ({
      name: data.details.membership_info.name,
      lastName: '',
      email: data.details.membership_info.email || '',
      phone: data.channel_routing.phone || '',
      usage: 'personal',
    }),
    [],
  );

  const handleInviteByChannel = useCallback(
    async (data: Invite, channel: InviteChannel) => {
      const config = CHANNEL_CONFIG[channel];
      const alias =
        channel === 'whatsapp'
          ? data.channel_routing.phone
          : data.channel_routing.email;

      if (!alias) {
        const missingField = channel === 'whatsapp' ? 'teléfono' : 'email';
        toast.error(
          `Invitación inválida: falta el ${missingField} requerido para este canal`,
        );
        throw new Error(`Missing ${missingField} for ${channel} invite`);
      }

      const signup = createSignupData(data);

      setLocalStorageItem('signup', signup);
      setLocalStorageItem('authFlow', 'signup');

      try {
        await fetchAuthRegistration(config.provider, alias);
        navigate({ to: `${config.otpRoute}?alias=${alias}` });
      } catch (error) {
        console.error(`Error processing ${channel} invite:`, error);
        toast.error(`Error al procesar invitación por ${channel}`);
        throw error;
      }
    },
    [navigate, createSignupData],
  );

  const processInvite = useCallback(
    async (data: Invite) => {
      try {
        const channel = data.channel as InviteChannel;

        if (!CHANNEL_CONFIG[channel]) {
          toast.error('Canal de invitación no soportado');
          return;
        }

        await handleInviteByChannel(data, channel);
      } catch (error) {
        console.error('Error processing invite:', error);
      }
    },
    [handleInviteByChannel],
  );

  return {
    processInvite,
  };
};
